import type { SemanticMatch } from '../domain/types'
import type { SemanticRouteDecision } from '../engine/semanticRouting'
import type {
  EmbeddingErrorCode,
  SetupProgress,
} from './protocol'

export type SemanticRuntimeState =
  | { phase: 'idle' }
  | { phase: 'scheduled' }
  | {
      phase: 'loading'
      progress: SetupProgress
      fallback?: string
    }
  | { phase: 'ready'; device: string }
  | {
      phase: 'deferred'
      reason:
        | 'hidden'
        | 'offline'
        | 'save-data'
        | 'user-paused'
    }
  | {
      phase: 'error'
      code: EmbeddingErrorCode
      message: string
      retryable: boolean
    }

export type SemanticAnalysisState =
  | { phase: 'idle'; revision: number }
  | { phase: 'waiting'; revision: number }
  | {
      phase: 'running'
      revision: number
      requestId: string
    }
  | {
      phase: 'complete'
      revision: number
      requestId: string
      device: string
      prompt: string
    }
  | {
      phase: 'error'
      revision: number
      message: string
    }

export type SemanticCoordinatorState = {
  runtime: SemanticRuntimeState
  analysis: SemanticAnalysisState
  matches: SemanticMatch[]
  routeDecision: SemanticRouteDecision | null
}

export type SemanticCoordinatorAction =
  | { type: 'preload-scheduled' }
  | { type: 'preload-progress'; progress: SetupProgress }
  | { type: 'runtime-ready'; device: string }
  | {
      type: 'runtime-deferred'
      reason: Extract<
        SemanticRuntimeState,
        { phase: 'deferred' }
      >['reason']
    }
  | {
      type: 'runtime-failed'
      code: EmbeddingErrorCode
      message: string
      retryable: boolean
    }
  | {
      type: 'runtime-fallback'
      failedDevice: string
      nextDevice: string
    }
  | {
      type: 'input-changed'
      revision: number
      hasPrompt: boolean
    }
  | {
      type: 'analysis-started'
      revision: number
      requestId: string
    }
  | {
      type: 'analysis-succeeded'
      revision: number
      requestId: string
      device: string
      prompt: string
      matches: SemanticMatch[]
      routeDecision: SemanticRouteDecision
    }
  | {
      type: 'analysis-failed'
      revision: number
      requestId: string
      message: string
    }
  | { type: 'disposed' }

export const INITIAL_SEMANTIC_STATE: SemanticCoordinatorState = {
  runtime: { phase: 'idle' },
  analysis: { phase: 'idle', revision: 0 },
  matches: [],
  routeDecision: null,
}

export function semanticCoordinatorReducer(
  state: SemanticCoordinatorState,
  action: SemanticCoordinatorAction,
): SemanticCoordinatorState {
  switch (action.type) {
    case 'preload-scheduled':
      return {
        ...state,
        runtime: { phase: 'scheduled' },
      }
    case 'preload-progress':
      return {
        ...state,
        runtime: {
          phase: 'loading',
          progress: action.progress,
          fallback:
            state.runtime.phase === 'loading'
              ? state.runtime.fallback
              : undefined,
        },
      }
    case 'runtime-ready':
      return {
        ...state,
        runtime: { phase: 'ready', device: action.device },
      }
    case 'runtime-deferred':
      return {
        ...state,
        runtime: {
          phase: 'deferred',
          reason: action.reason,
        },
      }
    case 'runtime-failed':
      return {
        ...state,
        runtime: {
          phase: 'error',
          code: action.code,
          message: action.message,
          retryable: action.retryable,
        },
      }
    case 'runtime-fallback':
      return {
        ...state,
        runtime: {
          phase: 'loading',
          progress:
            state.runtime.phase === 'loading'
              ? state.runtime.progress
              : {
                  status: 'indeterminate',
                  phase: 'model',
                },
          fallback: `Using ${action.nextDevice} after ${action.failedDevice} was unavailable`,
        },
      }
    case 'input-changed':
      return {
        ...state,
        analysis: action.hasPrompt
          ? {
              phase: 'waiting',
              revision: action.revision,
            }
          : {
              phase: 'idle',
              revision: action.revision,
            },
        matches: [],
        routeDecision: null,
      }
    case 'analysis-started':
      if (action.revision !== state.analysis.revision) {
        return state
      }
      return {
        ...state,
        analysis: {
          phase: 'running',
          revision: action.revision,
          requestId: action.requestId,
        },
      }
    case 'analysis-succeeded':
      if (
        state.analysis.phase !== 'running' ||
        action.revision !== state.analysis.revision ||
        action.requestId !== state.analysis.requestId
      ) {
        return state
      }
      return {
        ...state,
        analysis: {
          phase: 'complete',
          revision: action.revision,
          requestId: action.requestId,
          device: action.device,
          prompt: action.prompt,
        },
        matches: action.matches,
        routeDecision: action.routeDecision,
      }
    case 'analysis-failed':
      if (
        state.analysis.phase !== 'running' ||
        action.revision !== state.analysis.revision ||
        action.requestId !== state.analysis.requestId
      ) {
        return state
      }
      return {
        ...state,
        analysis: {
          phase: 'error',
          revision: action.revision,
          message: action.message,
        },
      }
    case 'disposed':
      return INITIAL_SEMANTIC_STATE
  }
}
